import "./style.css";
import { BabylonRigRuntime } from "./babylonRig";
import { MicFeatureStream } from "./audio";
import { interpolateMotionFrames, MotionFrameSmoother } from "./motionSmoothing";
import { GestureOnnxRuntime } from "./onnxRuntime";
import { TextFeatureTimeline } from "./textFeatures";

const canvas = requireElement<HTMLCanvasElement>("#renderCanvas");
const textPromptEl = requireElement<HTMLTextAreaElement>("#textPrompt");
const startButton = requireElement<HTMLButtonElement>("#startButton");
const testCaseAButton = requireElement<HTMLButtonElement>("#testCaseAButton");
const testCaseBButton = requireElement<HTMLButtonElement>("#testCaseBButton");
const testCaseCButton = requireElement<HTMLButtonElement>("#testCaseCButton");
const groundTruthAButton = requireElement<HTMLButtonElement>("#groundTruthAButton");
const groundTruthBButton = requireElement<HTMLButtonElement>("#groundTruthBButton");
const groundTruthCButton = requireElement<HTMLButtonElement>("#groundTruthCButton");
const debugPoseButton = requireElement<HTMLButtonElement>("#debugPoseButton");
const stopButton = requireElement<HTMLButtonElement>("#stopButton");
const statusEl = requireElement<HTMLElement>("#status");
const stepMsEl = requireElement<HTMLElement>("#stepMs");
const cacheFramesEl = requireElement<HTMLElement>("#cacheFrames");
const generatedFramesEl = requireElement<HTMLElement>("#generatedFrames");
const tickCountEl = requireElement<HTMLElement>("#tickCount");
const lastErrorEl = requireElement<HTMLElement>("#lastError");

const rig = new BabylonRigRuntime(canvas);
const mic = new MicFeatureStream();
const runtime = new GestureOnnxRuntime();
const motionSmoother = new MotionFrameSmoother();
const testCaseButtons = [testCaseAButton, testCaseBButton, testCaseCButton];
const groundTruthButtons = [groundTruthAButton, groundTruthBButton, groundTruthCButton];
let testCases: RuntimeTestCase[] = [];
let timer: number | null = null;
let inFlight = false;
let ticks = 0;
let generatedTicks = 0;
let ready = false;
let textTimeline = new TextFeatureTimeline(textPromptEl.value);
let motionQueue: Float32Array<ArrayBufferLike>[] = [];
let lastSmoothedFrame: Float32Array<ArrayBufferLike> | null = null;

void bootstrap().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  console.error(error);
  lastErrorEl.textContent = message;
  setStatus("load error");
});

async function bootstrap(): Promise<void> {
  setStatus("loading config");
  const config = await loadRuntimeConfig();
  console.info("ONNX model version", {
    version: config.modelVersion,
    modelBaseUrl: config.modelBaseUrl,
    audioEncoder: config.audioEncoder,
    transformerKv: config.transformerKv,
    tokenDecoder: config.tokenDecoder
  });
  if (config.avatarUrl) {
    setStatus("loading avatar");
    await rig.loadAvatar(config.avatarUrl);
  }
  setStatus("loading models");
  await runtime.load(config);
  testCases = await resolveTestCases(config);
  configureTestCaseButtons(testCases);
  ready = true;
  startButton.disabled = false;
  setTestCaseButtonsDisabled(false);
  setGroundTruthButtonsDisabled(false);
  debugPoseButton.disabled = false;
  setStatus("ready");
}

startButton.addEventListener("click", async () => {
  await startStreaming({ kind: "mic" });
});

testCaseButtons.forEach((button, index) => {
  button.addEventListener("click", async () => {
    const testCase = testCases[index];
    if (testCase) {
      await startStreaming({ kind: "test-case", testCase });
    }
  });
});

groundTruthButtons.forEach((button, index) => {
  button.addEventListener("click", async () => {
    const testCase = testCases[index];
    if (testCase) {
      await playGroundTruthMotion(testCase);
    }
  });
});

debugPoseButton.addEventListener("click", () => {
  startDebugPose();
});

stopButton.addEventListener("click", () => {
  void stopStreaming();
  setStatus("stopped");
});

async function startStreaming(source: StreamSource): Promise<void> {
  if (!ready || !runtime.loaded) {
    lastErrorEl.textContent = "ONNX sessions are still loading";
    setStatus("not ready");
    return;
  }
  startButton.disabled = true;
  setTestCaseButtonsDisabled(true);
  setGroundTruthButtonsDisabled(true);
  debugPoseButton.disabled = true;
  try {
    setStatus(source.kind === "mic" ? "requesting mic" : `loading ${source.testCase.label}`);
    if (source.kind === "mic") {
      await mic.start();
    } else {
      if (source.testCase.text) {
        textPromptEl.value = source.testCase.text;
      }
      await mic.startElement(source.testCase.audioUrl);
    }
    runtime.reset();
    resetMotionPlayback();
    textTimeline = new TextFeatureTimeline(textPromptEl.value);
    rig.resetPose();
    ticks = 0;
    generatedTicks = 0;
    tickCountEl.textContent = "0";
    lastErrorEl.textContent = "-";
    stopButton.disabled = false;
    setStatus(source.kind === "mic" ? "streaming mic" : `streaming ${source.testCase.label}`);
    timer = window.setInterval(() => {
      void playbackTick();
    }, 16);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(error);
    lastErrorEl.textContent = message;
    setStatus("start error");
    startButton.disabled = false;
    setTestCaseButtonsDisabled(false);
    setGroundTruthButtonsDisabled(false);
    debugPoseButton.disabled = false;
    stopButton.disabled = true;
  }
}

function startDebugPose(): void {
  if (!ready) {
    lastErrorEl.textContent = "Runtime is still loading";
    setStatus("not ready");
    return;
  }
  clearTimer();
  runtime.reset();
  resetMotionPlayback();
  textTimeline = new TextFeatureTimeline(textPromptEl.value);
  rig.resetPose();
  ticks = 0;
  generatedTicks = 0;
  tickCountEl.textContent = "0";
  lastErrorEl.textContent = "-";
  startButton.disabled = true;
  setTestCaseButtonsDisabled(true);
  setGroundTruthButtonsDisabled(true);
  debugPoseButton.disabled = true;
  stopButton.disabled = false;
  setStatus("debug pose");
  timer = window.setInterval(() => {
    ticks += 1;
    tickCountEl.textContent = String(ticks);
    rig.applyDebugPose();
  }, 33);
}

async function playGroundTruthMotion(testCase: RuntimeTestCase): Promise<void> {
  if (!ready) {
    lastErrorEl.textContent = "Runtime is still loading";
    setStatus("not ready");
    return;
  }
  clearTimer();
  await mic.stop();
  startButton.disabled = true;
  setTestCaseButtonsDisabled(true);
  setGroundTruthButtonsDisabled(true);
  debugPoseButton.disabled = true;
  stopButton.disabled = false;
  rig.resetPose();
  ticks = 0;
  tickCountEl.textContent = "0";
  lastErrorEl.textContent = "-";
  setStatus(`loading GT ${testCase.id}`);
  try {
    const clip = await loadGroundTruthMotion(testCase);
    const frameStride = clip.jointCount * clip.channels;
    const frameMs = Math.max(1, Math.round(1000 / Math.max(clip.fps, 1)));
    console.info("Ground truth motion loaded", {
      id: testCase.id,
      label: testCase.label,
      motionUrl: resolveMotionUrl(testCase),
      fps: clip.fps,
      frames: clip.frames,
      jointCount: clip.jointCount,
      channels: clip.channels
    });
    setStatus(`GT ${testCase.id}`);
    timer = window.setInterval(() => {
      const frameIndex = ticks % clip.frames;
      const offset = frameIndex * frameStride;
      const frame = clip.values.slice(offset, offset + frameStride);
      ticks += 1;
      tickCountEl.textContent = String(ticks);
      generatedFramesEl.textContent = String(ticks);
      cacheFramesEl.textContent = "-";
      stepMsEl.textContent = `GT ${(frameIndex + 1).toString()}/${clip.frames.toString()}`;
      rig.applyMotionFrame(frame);
    }, frameMs);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(error);
    lastErrorEl.textContent = message;
    setStatus("GT load error");
    startButton.disabled = false;
    setTestCaseButtonsDisabled(false);
    setGroundTruthButtonsDisabled(false);
    debugPoseButton.disabled = false;
    stopButton.disabled = true;
  }
}

async function playbackTick(): Promise<void> {
  ticks += 1;
  tickCountEl.textContent = String(ticks);
  const frame = motionQueue.shift();
  if (frame) {
    rig.applyMotionFrame(frame);
  }
  if (!inFlight && motionQueue.length < 2) {
    await inferNextMotionFrame();
  }
}

async function inferNextMotionFrame(): Promise<void> {
  inFlight = true;
  setStatus("inferencing");
  try {
    const frameIndex = generatedTicks;
    const waveform = mic.nextWaveformForFrame(frameIndex, runtime.audioSamples);
    if (!waveform) {
      setStatus("buffering audio");
      return;
    }
    const encodedFeature = await runtime.encodeAudio(waveform);
    const audioFeature = encodedFeature ?? mic.nextFeature(runtime.audioDim);
    const textFeature = textTimeline.featureAtFrame(frameIndex);
    const result = await runtime.step(audioFeature, textFeature);
    if (result.motion) {
      enqueueMotionFrame(result.motion);
    }
    generatedTicks += 1;
    stepMsEl.textContent = `${result.stats.stepMs.toFixed(2)} ms (kv ${result.stats.kvMs.toFixed(2)} / dec ${result.stats.decodeMs.toFixed(2)})`;
    cacheFramesEl.textContent = String(result.stats.cacheFrames);
    generatedFramesEl.textContent = String(result.stats.generatedFrames);
    setStatus("streaming");
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(error);
    lastErrorEl.textContent = message;
    setStatus("error");
    void stopStreaming();
  } finally {
    inFlight = false;
  }
}

function enqueueMotionFrame(frame: Float32Array<ArrayBufferLike>): void {
  const smoothed = motionSmoother.smooth(frame);
  if (lastSmoothedFrame) {
    motionQueue.push(interpolateMotionFrames(lastSmoothedFrame, smoothed, 0.5));
  }
  motionQueue.push(smoothed);
  lastSmoothedFrame = smoothed;
}

function resetMotionPlayback(): void {
  motionQueue = [];
  lastSmoothedFrame = null;
  motionSmoother.reset();
}

function setStatus(value: string): void {
  statusEl.textContent = value;
}

function requireElement<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

async function stopStreaming(): Promise<void> {
  clearTimer();
  await mic.stop();
  resetMotionPlayback();
  rig.resetPose();
  startButton.disabled = !ready;
  setTestCaseButtonsDisabled(!ready);
  setGroundTruthButtonsDisabled(!ready);
  debugPoseButton.disabled = !ready;
  stopButton.disabled = true;
}

function clearTimer(): void {
  if (timer !== null) {
    window.clearInterval(timer);
    timer = null;
  }
}

async function loadRuntimeConfig(): Promise<{
  modelVersion: string;
  modelBaseUrl?: string;
  audioEncoder?: string;
  transformerKv: string;
  tokenDecoder: string;
  avatarUrl?: string;
  samplingTemperature?: number;
  topK?: number;
  repetitionPenalty?: number;
  testCases?: RuntimeTestCase[];
  testCasesManifestUrl?: string;
}> {
  const response = await fetch("/runtime-config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load runtime config: ${response.status}`);
  }
  const config = (await response.json()) as {
    modelBaseUrl?: string;
    audioEncoderUrl?: string;
    transformerKvUrl?: string;
    tokenDecoderUrl?: string;
    avatarUrl?: string;
    samplingTemperature?: number;
    topK?: number;
    repetitionPenalty?: number;
    testCases?: RuntimeTestCase[];
    testCasesManifestUrl?: string;
  };
  const base = config.modelBaseUrl?.replace(/\/$/, "");
  const audioEncoder = config.audioEncoderUrl || undefined;
  const transformerKv = config.transformerKvUrl ?? (base ? `${base}/transformer_kv_step.onnx` : undefined);
  const tokenDecoder = config.tokenDecoderUrl ?? (base ? `${base}/token_decoder_dynamic.onnx` : undefined);
  if (!transformerKv || !tokenDecoder) {
    throw new Error("runtime-config.json must define modelBaseUrl or explicit transformerKvUrl/tokenDecoderUrl");
  }
  return {
    modelVersion: inferModelVersion(base, transformerKv),
    modelBaseUrl: base,
    audioEncoder,
    transformerKv,
    tokenDecoder,
    avatarUrl: config.avatarUrl,
    samplingTemperature: config.samplingTemperature,
    topK: config.topK,
    repetitionPenalty: config.repetitionPenalty,
    testCases: config.testCases,
    testCasesManifestUrl: config.testCasesManifestUrl
  };
}

type StreamSource =
  | { kind: "mic" }
  | { kind: "test-case"; testCase: RuntimeTestCase };

interface RuntimeTestCase {
  id: string;
  label: string;
  audioUrl: string;
  motionUrl?: string;
  text?: string;
}

interface GroundTruthMotionClip {
  fps: number;
  frames: number;
  jointCount: number;
  channels: number;
  values: Float32Array<ArrayBufferLike>;
}

function configureTestCaseButtons(cases: RuntimeTestCase[]): void {
  testCaseButtons.forEach((button, index) => {
    const testCase = cases[index];
    button.textContent = testCase?.label ?? `Test ${String.fromCharCode(65 + index)}`;
    button.disabled = !ready || !testCase;
  });
  groundTruthButtons.forEach((button, index) => {
    const testCase = cases[index];
    button.textContent = testCase ? `GT ${testCase.id}` : `GT ${String.fromCharCode(65 + index)}`;
    button.disabled = !ready || !testCase;
  });
}

function setTestCaseButtonsDisabled(disabled: boolean): void {
  testCaseButtons.forEach((button, index) => {
    button.disabled = disabled || !testCases[index];
  });
}

function setGroundTruthButtonsDisabled(disabled: boolean): void {
  groundTruthButtons.forEach((button, index) => {
    button.disabled = disabled || !testCases[index];
  });
}

async function resolveTestCases(config: {
  modelBaseUrl?: string;
  testCases?: RuntimeTestCase[];
  testCasesManifestUrl?: string;
}): Promise<RuntimeTestCase[]> {
  const manifestCases = await loadTestCaseManifest(config);
  const sourceCases = manifestCases.length ? manifestCases : config.testCases;
  if (sourceCases?.length) {
    return sourceCases.slice(0, 3).map((testCase) => ({
      ...testCase,
      motionUrl: testCase.motionUrl ?? inferMotionUrl(testCase.audioUrl)
    }));
  }
  const base = inferStaticModelsBase(config.modelBaseUrl);
  return ["A", "B", "C"].map((label) => ({
    id: label,
    label: `Test ${label}`,
    audioUrl: `${base}/beat2-test/case-${label.toLowerCase()}/audio.wav`,
    motionUrl: `${base}/beat2-test/case-${label.toLowerCase()}/motion.json`
  }));
}

async function loadTestCaseManifest(config: {
  modelBaseUrl?: string;
  testCasesManifestUrl?: string;
}): Promise<RuntimeTestCase[]> {
  const manifestUrl =
    config.testCasesManifestUrl ?? `${inferStaticModelsBase(config.modelBaseUrl)}/beat2-test/frontend_test_cases.json`;
  try {
    const response = await fetch(manifestUrl, { cache: "no-store" });
    if (!response.ok) {
      console.warn("BEAT2 test case manifest unavailable", { manifestUrl, status: response.status });
      return [];
    }
    const payload = (await response.json()) as { testCases?: RuntimeTestCase[] };
    console.info("BEAT2 test case manifest loaded", {
      manifestUrl,
      cases: payload.testCases?.map((item) => ({
        id: item.id,
        hasText: Boolean(item.text?.trim()),
        audioUrl: item.audioUrl,
        motionUrl: item.motionUrl
      }))
    });
    return payload.testCases ?? [];
  } catch (error) {
    console.warn("BEAT2 test case manifest load failed", { manifestUrl, error });
    return [];
  }
}

async function loadGroundTruthMotion(testCase: RuntimeTestCase): Promise<GroundTruthMotionClip> {
  const motionUrl = resolveMotionUrl(testCase);
  const response = await fetch(motionUrl, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load ground truth motion ${testCase.id}: ${response.status}`);
  }
  const payload = (await response.json()) as {
    fps?: number;
    frames?: number;
    jointCount?: number;
    channels?: number;
    values?: number[];
  };
  const fps = finitePositive(payload.fps, 30);
  const jointCount = Math.trunc(finitePositive(payload.jointCount, 56));
  const channels = Math.trunc(finitePositive(payload.channels, 3));
  const values = new Float32Array(payload.values ?? []);
  const inferredFrames = Math.floor(values.length / Math.max(1, jointCount * channels));
  const frames = Math.min(Math.trunc(finitePositive(payload.frames, inferredFrames)), inferredFrames);
  if (jointCount !== 56 || channels !== 3 || frames <= 0 || values.length < frames * jointCount * channels) {
    throw new Error(`Invalid ground truth motion payload for ${testCase.id}`);
  }
  return { fps, frames, jointCount, channels, values };
}

function resolveMotionUrl(testCase: RuntimeTestCase): string {
  return testCase.motionUrl ?? inferMotionUrl(testCase.audioUrl);
}

function inferMotionUrl(audioUrl: string): string {
  return audioUrl.replace(/\/[^/]*$/u, "/motion.json");
}

function finitePositive(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

function inferStaticModelsBase(modelBaseUrl: string | undefined): string {
  const fallback = "https://cdn.bowong.cc/static/models";
  if (!modelBaseUrl) {
    return fallback;
  }
  const match = modelBaseUrl.match(/^(.*\/static\/models)(?:\/[^/?#]+)?/);
  return match?.[1] ?? fallback;
}

function inferModelVersion(modelBaseUrl: string | undefined, transformerKvUrl: string): string {
  const source = modelBaseUrl || transformerKvUrl;
  const match = source.match(/\/static\/models\/([^/?#]+)/);
  if (match) {
    return match[1];
  }
  return "custom";
}
